import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, Rocket, Send, Sparkles, X, Radio, Plus } from "lucide-react";
import { http, errText } from "@/lib/http";
import { toast } from "sonner";

/*
 * Koko — the proactive personal orchestrator.
 * - Floating orb bottom-right on every authenticated page.
 * - Opens with a digest ("here's what I noticed since you were last here")
 *   and one daily discovery question to deepen the business profile.
 * - User chats; Koko proposes 1-3 role delegations with chain support.
 * - Confirm → dispatch via OpenClaw or queue it.
 */

const SID_KEY = "mh-koko-sid";

const Orb = ({ open, onClick, pulse }) => (
  <button
    onClick={onClick}
    data-testid="koko-orb"
    aria-label={open ? "Close Koko" : "Open Koko"}
    className="fixed bottom-5 right-5 lg:bottom-6 lg:right-6 z-[70] group"
    style={{ display: open ? "none" : undefined }}
  >
    <span className="relative block w-14 h-14 rounded-full bg-black border border-white/15 shadow-[0_6px_30px_-6px_rgba(212,255,58,0.55)] transition-transform duration-300 group-hover:scale-[1.04]">
      <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(212,255,58,0.45),transparent_60%)]" />
      <span className="absolute inset-[6px] rounded-full border border-[#D4FF3A]/40" />
      <span className="absolute inset-0 grid place-items-center">
        <DotCluster />
      </span>
      {pulse && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#D4FF3A] animate-ping" />}
      {pulse && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#D4FF3A]" />}
    </span>
    <span className="absolute right-16 top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] font-mono uppercase tracking-[0.22em] text-white/60 opacity-0 group-hover:opacity-100 transition-opacity">
      Ask Koko
    </span>
  </button>
);

const DotCluster = () => (
  <svg viewBox="0 0 32 32" className="w-7 h-7">
    {[
      [10, 10],
      [22, 10],
      [16, 22],
    ].map(([cx, cy], i) => (
      <circle
        key={i}
        cx={cx}
        cy={cy}
        r="3"
        fill="#D4FF3A"
        style={{ animation: `koko-pulse 2.4s ${i * 0.3}s ease-in-out infinite` }}
      />
    ))}
    <style>{`@keyframes koko-pulse { 0%,100% { opacity: 0.55; transform-origin: center; } 50% { opacity: 1; } }`}</style>
  </svg>
);

const RolePill = ({ status, department }) => (
  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-mono uppercase tracking-wider">
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: status === "live" ? "#D4FF3A" : status === "in_dev" ? "#FFB020" : "#5A5A66" }} />
    {department}
  </span>
);

const stateCopy = {
  proposed: { label: "Confirm & send", tone: "accent" },
  dispatched: { label: "Dispatched to OpenClaw", tone: "ok" },
  queued: { label: "Queued · ready to send", tone: "warn" },
  scheduled: { label: "Scheduled · waiting for upstream receipt", tone: "warn" },
  failed: { label: "Dispatch failed", tone: "err" },
  dismissed: { label: "Dismissed", tone: "mute" },
};

const Delegation = ({ d, sessionId, messageId, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [brief, setBrief] = useState(d.task_brief);
  const [busy, setBusy] = useState(false);
  const copy = stateCopy[d.state] || stateCopy.proposed;
  const chainedParent = d.chain_after_id ? "chained" : null;
  const confirm = async () => {
    setBusy(true);
    try {
      const r = (await http.post("/koko/delegate", { session_id: sessionId, message_id: messageId, delegation_id: d.id, task_brief: brief })).data;
      onUpdate(r.delegation);
      if (r.delegation.state === "dispatched") toast.success(`${d.role_name} is running your task`);
      else if (r.delegation.state === "queued") toast(`Queued to ${d.role_name}`, { description: r.delegation.error || "Will run when you turn on OpenClaw / the role." });
      else if (r.delegation.state === "failed") toast.error(`${d.role_name} failed`, { description: r.delegation.error });
    } catch (e) {
      toast.error(errText(e, "Could not delegate"));
    }
    setBusy(false);
    setEditing(false);
  };
  const dismiss = async () => {
    try {
      await http.post("/koko/dismiss", { session_id: sessionId, message_id: messageId, delegation_id: d.id });
      onUpdate({ ...d, state: "dismissed" });
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.015] p-3 space-y-2.5" data-testid={`koko-deleg-${d.id}`}>
      <div className="flex items-start gap-2.5">
        <span className="w-8 h-8 rounded-full bg-[#D4FF3A]/15 border border-[#D4FF3A]/30 grid place-items-center shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-[#D4FF3A]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="font-display text-base leading-tight truncate">{d.role_name}</div>
            <RolePill status={d.status} department={d.department} />
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 italic">{d.why}</div>
          {chainedParent && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md border border-[#00E5FF]/30 bg-[#00E5FF]/[0.06] text-[10px] font-mono uppercase tracking-wider text-[#00E5FF]" data-testid={`koko-chain-${d.id}`}>
              <Radio className="w-2.5 h-2.5" />
              Chained · auto-fires on upstream receipt
            </div>
          )}
        </div>
      </div>
      {editing ? (
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)} data-testid={`koko-brief-edit-${d.id}`}
          className="w-full min-h-[76px] rounded-xl bg-black/40 border border-white/15 p-2.5 text-sm outline-none focus:border-[#D4FF3A]/60 font-mono resize-y" />
      ) : (
        <div className="text-sm leading-relaxed whitespace-pre-wrap text-white/85 bg-black/30 border border-white/[0.06] rounded-xl p-2.5" data-testid={`koko-brief-${d.id}`}>{brief}</div>
      )}
      {d.state === "proposed" ? (
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={confirm} disabled={busy || !brief.trim()} data-testid={`koko-confirm-${d.id}`}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#D4FF3A] text-black text-xs font-mono uppercase tracking-wider disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
            {copy.label}
          </button>
          <button onClick={() => setEditing((e) => !e)} data-testid={`koko-edit-${d.id}`}
            className="h-8 px-3 rounded-full border border-white/15 text-xs font-mono uppercase tracking-wider hover:border-white/40">
            {editing ? "Done editing" : "Edit brief"}
          </button>
          <button onClick={dismiss} data-testid={`koko-dismiss-${d.id}`}
            className="h-8 px-3 rounded-full text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">
            Not now
          </button>
        </div>
      ) : (
        <StateBadge state={d.state} error={d.error} runId={d.mh_run_id} />
      )}
    </div>
  );
};

const StateBadge = ({ state, error, runId }) => {
  const copy = stateCopy[state] || stateCopy.proposed;
  const tone = {
    ok: "bg-[#D4FF3A]/15 border-[#D4FF3A]/40 text-[#D4FF3A]",
    warn: "bg-[#FFB020]/10 border-[#FFB020]/35 text-[#FFB020]",
    err: "bg-[#FF3B5C]/10 border-[#FF3B5C]/40 text-[#FF3B5C]",
    mute: "border-white/10 text-muted-foreground",
  }[copy.tone] || "border-white/10 text-foreground";
  const Icon = state === "dispatched" ? Check : state === "failed" ? X : state === "queued" ? Radio : null;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-mono uppercase tracking-wider ${tone}`} data-testid={`koko-state-${runId || state}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {copy.label}
      {error && state !== "queued" && <span className="normal-case tracking-normal ml-1 opacity-80">· {error}</span>}
    </div>
  );
};

const DigestView = ({ digest, roles, nav, onClose, onAsk, onDiscoveryAnswer }) => {
  const [answer, setAnswer] = useState("");
  const [savingGap, setSavingGap] = useState(false);
  if (!digest) {
    return <div className="font-mono text-xs text-muted-foreground">Opening the house…</div>;
  }
  const { pending_approvals, recent_receipts = [], blocked_receipts = [], idle_live_roles = [], profile_gaps = [], tailored_count } = digest;
  const nothingTodo = !pending_approvals && !recent_receipts.length && !blocked_receipts.length && !idle_live_roles.length && !profile_gaps.length;
  const gap = profile_gaps[0];
  const submitGap = async () => {
    if (!answer.trim() || !gap) return;
    setSavingGap(true);
    await onDiscoveryAnswer(gap, answer.trim());
    setAnswer("");
    setSavingGap(false);
  };
  return (
    <div className="space-y-3" data-testid="koko-digest">
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#D4FF3A]/[0.08] via-transparent to-transparent p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Since you were last here</div>
        <div className="font-display text-xl leading-tight">
          {nothingTodo
            ? "Everything's quiet — no new receipts, no pending approvals."
            : `Here's what I noticed across your ${tailored_count || 0} tailored roles.`}
        </div>
        {roles.length === 0 && (
          <button onClick={() => { onClose(); nav("/setup"); }} data-testid="koko-no-roles-cta" className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[#D4FF3A] hover:underline">
            No tailored roles yet · finish setup <ArrowUpRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {pending_approvals > 0 && (
        <button onClick={() => { onClose(); nav("/approvals"); }} data-testid="koko-digest-approvals"
          className="w-full text-left rounded-2xl border border-[#FFB020]/35 bg-[#FFB020]/[0.06] p-3.5 hover:bg-[#FFB020]/[0.1] transition-colors flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-[#FFB020]/15 border border-[#FFB020]/40 grid place-items-center shrink-0">
            <Check className="w-4 h-4 text-[#FFB020]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{pending_approvals} approval{pending_approvals === 1 ? "" : "s"} waiting on you</div>
            <div className="text-xs text-muted-foreground">Review and approve, edit, or reject.</div>
          </div>
          <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
        </button>
      )}

      {blocked_receipts.length > 0 && (
        <div className="rounded-2xl border border-[#FF3B5C]/35 bg-[#FF3B5C]/[0.06] p-3.5" data-testid="koko-digest-blocked">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3B5C] mb-1">Blocked · needs you</div>
          {blocked_receipts.slice(0, 3).map((r) => (
            <div key={r.run_id} className="text-xs text-white/85 mt-1">
              <span className="text-white">{r.agent_name || r.agent_slug}</span> · {r.summary || "No summary"}
            </div>
          ))}
        </div>
      )}

      {idle_live_roles.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3.5" data-testid="koko-digest-idle">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Live but idle · 24h+</div>
          <div className="flex flex-wrap gap-1.5">
            {idle_live_roles.map((r) => (
              <button key={r.slug} onClick={() => onAsk(`What should ${r.name} do today?`)} data-testid={`koko-idle-${r.slug}`}
                className="px-2.5 py-1 rounded-full border border-white/15 text-xs hover:border-[#D4FF3A]/50 transition-colors">
                {r.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {recent_receipts.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3.5" data-testid="koko-digest-receipts">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Latest receipts</div>
          <div className="space-y-2">
            {recent_receipts.slice(0, 3).map((r) => (
              <div key={r.run_id} className="text-xs">
                <div className="flex items-center gap-2 text-white/90">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.outcome === "blocked" ? "#FF3B5C" : r.outcome === "needs_review" ? "#FFB020" : "#D4FF3A" }} />
                  <span className="font-medium">{r.agent_name || r.agent_slug}</span>
                  {r.minutes_saved ? <span className="text-muted-foreground">· {r.minutes_saved}m saved</span> : null}
                </div>
                <div className="text-muted-foreground mt-0.5 line-clamp-2">{r.summary || "(no summary)"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {gap && (
        <div className="rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/[0.04] p-3.5 space-y-2.5" data-testid="koko-discovery">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 grid place-items-center shrink-0">
              <Sparkles className="w-3.5 h-3.5 text-[#00E5FF]" />
            </span>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00E5FF]">One question · {gap.department_name}</div>
          </div>
          <div className="text-sm text-white/90 leading-snug">{gap.question}</div>
          <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={2} placeholder="Short answer is fine…" data-testid="koko-discovery-input"
            className="w-full rounded-xl bg-black/40 border border-white/15 p-2.5 text-sm outline-none focus:border-[#00E5FF]/60 resize-none" />
          <div className="flex items-center gap-2">
            <button onClick={submitGap} disabled={savingGap || !answer.trim()} data-testid="koko-discovery-save"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#00E5FF] text-black text-xs font-mono uppercase tracking-wider disabled:opacity-40">
              {savingGap ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Save to profile
            </button>
            <button onClick={() => onDiscoveryAnswer(gap, "skipped")} className="h-8 px-3 rounded-full text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground" data-testid="koko-discovery-skip">
              Skip
            </button>
          </div>
        </div>
      )}

      <div className="pt-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Or tell me what to do</div>
      </div>
    </div>
  );
};

export const Panel = ({ onClose }) => {
  const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [roles, setRoles] = useState([]);
  const [digest, setDigest] = useState(null);
  const scroller = useRef(null);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    http.get("/koko/roles").then((r) => setRoles(r.data.roles || [])).catch(() => {});
    http.get("/koko/digest").then((r) => setDigest(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!sid) return;
    http.get(`/koko/history?session_id=${sid}`).then((r) => setMessages(r.data.messages || [])).catch(() => {});
  }, [sid]);
  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    const userMsg = { id: `local-${Date.now()}`, role: "user", text: text.trim(), at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setBusy(true);
    try {
      const r = (await http.post("/koko/chat", { message: text.trim(), session_id: sid, page: loc.pathname })).data;
      if (!sid) { setSid(r.session_id); localStorage.setItem(SID_KEY, r.session_id); }
      setMessages((m) => [...m, r.message]);
    } catch (e) {
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "dots", text: errText(e, "Koko couldn't answer right now."), delegations: [], error: true }]);
    }
    setBusy(false);
  };

  const updateDelegation = (messageId, next) => {
    setMessages((ms) => ms.map((m) => m.id === messageId ? { ...m, delegations: (m.delegations || []).map((d) => d.id === next.id ? next : d) } : m));
  };

  const reset = async () => {
    try {
      const r = (await http.post("/koko/session")).data;
      setSid(r.session_id); localStorage.setItem(SID_KEY, r.session_id);
      setMessages([]);
    } catch { localStorage.removeItem(SID_KEY); setSid(""); setMessages([]); }
  };

  const liveCount = useMemo(() => roles.filter((r) => r.status === "live").length, [roles]);

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-[75] bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200" data-testid="koko-backdrop" />
      <aside
        data-testid="koko-panel"
        className="fixed right-0 top-0 bottom-0 z-[76] w-full sm:w-[440px] bg-[#07070B] border-l border-white/10 flex flex-col shadow-[-20px_0_60px_-20px_rgba(0,0,0,0.9)] animate-in slide-in-from-right duration-300"
      >
        <header className="px-5 pt-5 pb-3 border-b border-white/[0.06] flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-black border border-[#D4FF3A]/30 grid place-items-center shrink-0 relative">
            <DotCluster />
            <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(212,255,58,0.3),transparent_60%)]" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-2xl leading-tight">Koko</h2>
            <p className="text-xs text-muted-foreground">
              Your proactive orchestrator · <span className="text-[#D4FF3A]">{liveCount}</span> live / {roles.length} tailored roles
            </p>
          </div>
          <button onClick={reset} title="Start a new conversation" data-testid="koko-new" className="w-9 h-9 rounded-full border border-white/10 grid place-items-center hover:border-white/30"><Plus className="w-4 h-4" /></button>
          <button onClick={onClose} data-testid="koko-close" className="w-9 h-9 rounded-full border border-white/10 grid place-items-center hover:border-white/30"><X className="w-4 h-4" /></button>
        </header>

        <div ref={scroller} className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="koko-stream">
          {messages.length === 0 && !busy && (
            <DigestView digest={digest} roles={roles} nav={nav} onClose={onClose} onAsk={send} onDiscoveryAnswer={async (gap, answer) => {
              try {
                await http.post("/koko/discovery/answer", { department: gap.department, topic: gap.topic, answer });
                // optimistically remove the gap from the digest so UI advances to next one
                setDigest((d) => d ? { ...d, profile_gaps: (d.profile_gaps || []).filter((g) => !(g.department === gap.department && g.topic === gap.topic)) } : d);
                toast.success(`Noted · saved to ${gap.department_name} profile`);
              } catch (e) { toast.error(errText(e)); }
            }} />
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "user" ? (
                <div className="max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap bg-[#D4FF3A]/[0.12] border border-[#D4FF3A]/25" data-testid={`koko-user-${m.id}`}>{m.text}</div>
              ) : (
                <div className="max-w-[94%] w-full space-y-2.5" data-testid={`koko-reply-${m.id}`}>
                  <div className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.error ? "border border-[#FF3B5C]/40 text-[#FF3B5C]" : "bg-white/[0.04] border border-white/[0.06]"}`}>{m.text}</div>
                  {(m.delegations || []).map((d) => (
                    <Delegation key={d.id} d={d} sessionId={sid} messageId={m.id} onUpdate={(nx) => updateDelegation(m.id, nx)} />
                  ))}
                  {m.clarify && <div className="text-xs text-muted-foreground italic px-3.5">↳ {m.clarify}</div>}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF3A]" style={{ animation: "koko-wave 1.2s 0s infinite" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF3A]" style={{ animation: "koko-wave 1.2s 0.15s infinite" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF3A]" style={{ animation: "koko-wave 1.2s 0.3s infinite" }} />
              </span>
              Koko is thinking…
              <style>{`@keyframes koko-wave { 0%,80%,100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-2px); } }`}</style>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="px-5 py-4 border-t border-white/[0.06] flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder="Tell Koko what to do…"
            rows={1}
            data-testid="koko-input"
            className="flex-1 min-h-[42px] max-h-[160px] py-2.5 px-3.5 rounded-2xl bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-[#D4FF3A]/60 resize-none"
          />
          <button type="submit" disabled={busy || !input.trim()} data-testid="koko-send"
            className="w-11 h-11 rounded-full bg-[#D4FF3A] text-black grid place-items-center disabled:opacity-40 hover:scale-105 transition-transform"><Send className="w-4 h-4" /></button>
        </form>
      </aside>
    </>
  );
};

export const Koko = () => {
  const [open, setOpen] = useState(false);
  // keyboard shortcut: cmd/ctrl-k opens Koko
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <>
      <Orb open={open} onClick={() => setOpen(true)} />
      {open && <Panel onClose={() => setOpen(false)} />}
    </>
  );
};

export default Koko;
